import {
  TagModel,
  TagscapeViewProperties,
  TagscapeViewModel,
  TagscapeViewSettings,
} from './types';
import type { PermissionContext } from '@ardoq/access-control';
import type { ContextShape, ScenarioModeState } from '@ardoq/data-model';
import type { StreamCollection } from 'streams/utils/streamUtils';
import { isTruthy } from '@ardoq/common-helpers';
import { referenceInterface } from '@ardoq/reference-interface';

import { type APITagAttributes, ArdoqId } from '@ardoq/api-types';
import { componentInterface } from '@ardoq/component-interface';
import { colors } from '@ardoq/design-tokens';
import { ensureContrast } from '@ardoq/color-helpers';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import type { I18nLocale } from '@ardoq/i18n';

const hasComponentsFromOtherWorkspace = (
  workspaceId: string,
  ids: Set<string>
) =>
  [...ids].some(
    id =>
      componentInterface.isComponent(id) &&
      componentInterface.getWorkspaceId(id) !== workspaceId
  );

const hasAllIds = (
  comps: { cid: string; id: string }[],
  ids: Set<string>
): boolean => {
  const allIds = new Set(comps.flatMap(c => [c.cid, c.id]));
  return [...ids].every(id => allIds.has(id));
};

const toViewModel = (
  tag: APITagAttributes,
  hoveredComponentId: ArdoqId | null,
  focusedComponentId: ArdoqId | null,
  draggedIds: Set<string> | null
): TagModel => {
  const referenceIds: string[] = tag.references ?? [];
  const refs = referenceIds
    .map(id => {
      const data = referenceInterface.get(id);
      if (!data) return null;
      const sourceName = data.source
        ? (componentInterface.getDisplayName(data.source) ?? 'Unknown')
        : 'Unknown';
      const targetName = data.target
        ? (componentInterface.getDisplayName(data.target) ?? 'Unknown')
        : 'Unknown';
      return {
        id: data._id,
        cid: id,
        name: `${sourceName} -> ${targetName}`,
        cssClassNames: referenceInterface.getCssClassNames(data._id) ?? '',
        isHovered: data._id === hoveredComponentId,
        isFocused: data._id === focusedComponentId,
      };
    })
    .filter(isTruthy);
  const componentIds: string[] = tag.components ?? [];
  const comps = componentIds
    .map(id => ({ id, data: componentInterface.get(id) }))
    .filter(({ data }) => data !== null)
    .map(({ id, data }) => ({
      id,
      cid: id,
      name: data!.name,
      typeId: data!.typeId ?? '',
      cssClassNames: componentInterface.getCssClassNames(id) ?? '',
      isHovered: id === hoveredComponentId,
      isFocused: id === focusedComponentId,
      color: ensureContrast(
        colors.white950,
        componentInterface.getComponentColor(id) || colors.black950
      ),
    }));

  return {
    cid: tag._id,
    name: tag.name,
    description: tag.description || '',
    parked: true,
    references: refs,
    components: comps,
    isUsed: comps.length > 0 || refs.length > 0,
    id: tag._id,
    droppable: draggedIds ? !hasAllIds(comps, draggedIds) : undefined,
  };
};

export type InteractionState = {
  hoveredComponentId: ArdoqId | null;
  focusedComponentId: ArdoqId | null;
  draggedIds: Set<string> | null;
  disableAddingTagToDraggedComponent: boolean;
};

export const toProps = ({
  interactionState,
  tags,
  context,
  permissionContext,
  activeScenario,
  viewState,
  i18nLocale,
}: {
  interactionState: InteractionState;
  tags: StreamCollection<APITagAttributes>;
  context: ContextShape;
  permissionContext: PermissionContext;
  activeScenario: ScenarioModeState;
  viewState: TagscapeViewSettings;
  i18nLocale: I18nLocale;
}): TagscapeViewProperties => {
  const workspaceId = context.workspaceId;
  const {
    hoveredComponentId,
    focusedComponentId,
    draggedIds,
    disableAddingTagToDraggedComponent,
  } = interactionState;

  const allTags = workspaceId
    ? tags.models
        .filter(tag => tag.rootWorkspace === workspaceId)
        .map(tag =>
          toViewModel(tag, hoveredComponentId, focusedComponentId, draggedIds)
        )
    : [];

  const visibleTags = viewState.excludeUnused
    ? allTags.filter(tag => tag.isUsed)
    : allTags;

  const viewModel: TagscapeViewModel = {
    tags: allTags,
    visibleTags,
    hasExcludedTags:
      allTags.length > 0 && allTags.length !== visibleTags.length,
    isEmptyView: visibleTags.length === 0,
    workspace: {
      id: workspaceId,
      isWriter: workspaceAccessControlInterface.canEditWorkspace(
        permissionContext,
        workspaceId,
        activeScenario
      ),
      hasComponents: !componentInterface.isWorkspaceEmpty(workspaceId),
    },
    disableAddingTagToDraggedComponent: draggedIds
      ? hasComponentsFromOtherWorkspace(workspaceId, draggedIds)
      : disableAddingTagToDraggedComponent,
  };
  return { viewModel, viewState, i18nLocale };
};
